import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase as envSupabase, createManualClient } from '../supabase';

const VisitorContext = createContext();

export const useVisitor = () => {
  const context = useContext(VisitorContext);
  if (!context) throw new Error('useVisitor must be used within a VisitorProvider');
  return context;
};

export const VisitorProvider = ({ children }) => {
  const [visitors, setVisitors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [isLoadingDb, setIsLoadingDb] = useState(false);

  // Auth & Connection State
  const [user, setUser] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [tablesMissing, setTablesMissing] = useState(false);
  const [credentials, setCredentials] = useState({
    url: import.meta.env.VITE_SUPABASE_URL || '',
    key: import.meta.env.VITE_SUPABASE_ANON_KEY || ''
  });
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const supabaseClientRef = useRef(envSupabase);

  const ipToNum = (ip) => {
    if (!ip) return 0;
    return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;
  };

  const lookupIp = useCallback(async (ip) => {
    if (!ip || !supabaseClientRef.current) return { org: 'Unknown' };
    try {
      const ipNum = ipToNum(ip);
      const { data, error } = await supabaseClientRef.current
        .rpc('find_organization', { search_ip: ipNum });
      
      if (!error && data) {
        return { org: data };
      }
    } catch (e) {
      console.warn('RPC Lookup failed', e);
    }
    return { org: 'Unknown' };
  }, []);

  const fetchData = async () => {
    if (!supabaseClientRef.current) return;
    try {
      // 1. Fetch Projects
      const { data: projectsData, error: pError } = await supabaseClientRef.current
        .from('projects_v1')
        .select('*');
      if (pError) throw pError;
      if (projectsData) setProjects(projectsData);

      // 2. Fetch Visitors
      const { data: visitorsData, error: vError } = await supabaseClientRef.current
        .from('visitors_v1')
        .select('*')
        .order('timestamp', { ascending: false });
      if (vError) throw vError;
      if (visitorsData) setVisitors(visitorsData);

      // 3. Fetch Blacklist
      const { data: blacklistData, error: bError } = await supabaseClientRef.current
        .from('blacklist_v1')
        .select('*')
        .order('created_at', { ascending: false });
      
      // Don't throw on blacklist error as table might not exist yet for old users
      if (blacklistData) setBlacklist(blacklistData);
      
      setTablesMissing(false);
    } catch (err) {
      console.error('Data Fetch Error:', err);
      if (err.code === '42P01' || err.message?.includes('does not exist')) {
        setTablesMissing(true);
        showToast('Database Error: Tables not found.', 'error');
      } else {
        showToast(`Error fetching data: ${err.message}`, 'error');
      }
    }
  };

  useEffect(() => {
    const initConnection = async () => {
      if (envSupabase) {
        supabaseClientRef.current = envSupabase;
        setIsConnected(true);
      } else {
        const localUrl = localStorage.getItem('sb_url');
        const localKey = localStorage.getItem('sb_key');
        if (localUrl && localKey && localUrl !== 'undefined') {
          const manualClient = createManualClient(localUrl, localKey);
          if (manualClient) {
            supabaseClientRef.current = manualClient;
            setCredentials({ url: localUrl, key: localKey });
            setIsConnected(true);
          }
        }
      }

      if (supabaseClientRef.current) {
        const { data: { session } } = await supabaseClientRef.current.auth.getSession();
        setUser(session?.user || null);
        if (session?.user) {
          fetchData();
        }
      }
    };
    initConnection();
  }, []);

  // Real-time updates
  useEffect(() => {
    if (!isConnected || !supabaseClientRef.current || !user || tablesMissing) return;

    const channel = supabaseClientRef.current
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'visitors_v1' },
        async (payload) => {
          const newVisit = payload.new;
          let finalOrg = newVisit.organization;
          if (!finalOrg) {
            const result = await lookupIp(newVisit.ip);
            finalOrg = result.org;
          }
          setVisitors((prev) => [{ ...newVisit, organization: finalOrg }, ...prev]);
          showToast('New visitor detected!', 'info');
        }
      )
      .subscribe();

    return () => {
      if (supabaseClientRef.current) supabaseClientRef.current.removeChannel(channel);
    };
  }, [isConnected, user, lookupIp, tablesMissing]);

  // --- Auth Functions ---
  const login = async (email, password, isSignUp = false) => {
    if (!supabaseClientRef.current) return { error: 'No client' };
    try {
      let result;
      if (isSignUp) {
        result = await supabaseClientRef.current.auth.signUp({ email, password });
      } else {
        result = await supabaseClientRef.current.auth.signInWithPassword({ email, password });
      }

      if (result.error) throw result.error;
      setUser(result.data.user);
      setTimeout(() => fetchData(), 500);
      showToast(isSignUp ? 'Account created & logged in!' : 'Logged in successfully');
      return { data: result.data };
    } catch (error) {
      showToast(error.message, 'error');
      return { error };
    }
  };

  const logout = async () => {
    if (supabaseClientRef.current) await supabaseClientRef.current.auth.signOut();
    setUser(null);
    setVisitors([]);
    setProjects([]);
    showToast('Logged out');
  };

  const connectManual = (url, key) => {
    if (!url || !key) return false;
    const client = createManualClient(url, key);
    if (client) {
      supabaseClientRef.current = client;
      localStorage.setItem('sb_url', url);
      localStorage.setItem('sb_key', key);
      setCredentials({ url, key });
      setIsConnected(true);
      showToast('Supabase connection initialized. Please login.');
      return true;
    }
    return false;
  };

  // --- Project Functions ---
  const addProject = async (project) => {
    if (!supabaseClientRef.current || !user) {
      showToast('You must be logged in', 'error');
      return false;
    }
    try {
      const { data, error } = await supabaseClientRef.current
        .from('projects_v1')
        .insert([{ ...project, user_id: user.id }])
        .select();

      if (error) throw error;
      if (data) {
        setProjects(prev => [...prev, data[0]]);
        showToast('Project added successfully');
        return true;
      }
    } catch (error) {
      if (error.code === '42P01') {
        setTablesMissing(true);
        showToast('Database Error: Table projects_v1 missing', 'error');
      } else {
        showToast(`Failed to add project: ${error.message}`, 'error');
      }
      return false;
    }
  };

  const removeProject = async (id) => {
    if (!supabaseClientRef.current) return;
    const { error } = await supabaseClientRef.current
      .from('projects_v1')
      .delete()
      .eq('id', id);
    if (!error) {
      setProjects(prev => prev.filter(p => p.id !== id));
      showToast('Project removed');
    } else {
      showToast('Failed to remove project', 'error');
    }
  };

  // --- Blacklist Functions ---
  const addToBlacklist = async (pattern, category = 'ISP') => {
    if (!supabaseClientRef.current || !user) return;
    try {
      const { data, error } = await supabaseClientRef.current
        .from('blacklist_v1')
        .insert([{ pattern, category, user_id: user.id }])
        .select();

      if (error) throw error;
      if (data) {
        setBlacklist(prev => [data[0], ...prev]);
        showToast(`Blocked "${pattern}"`);
        return true;
      }
    } catch (error) {
      console.error(error);
      showToast('Failed to add to blacklist', 'error');
      return false;
    }
  };

  const addBulkBlacklist = async (items) => {
    if (!supabaseClientRef.current || !user || items.length === 0) return;
    try {
      // Add user_id to all items
      const rows = items.map(i => ({ ...i, user_id: user.id }));
      
      // Use upsert or insert with ignoreDuplicates if supported, 
      // but standard insert with on conflict ignore is safest if schema allows.
      // Supabase JS client 'insert' with { ignoreDuplicates: true } handles this.
      const { data, error } = await supabaseClientRef.current
        .from('blacklist_v1')
        .insert(rows, { onConflict: 'pattern', ignoreDuplicates: true })
        .select();

      if (error) throw error;
      
      if (data) {
        // We need to re-fetch or smartly merge because data only contains successfully inserted rows
        // Simple approach: re-fetch or just append unique ones to local state
        // For accuracy with bulk ops, fetching fresh list is safer
        const { data: freshList } = await supabaseClientRef.current
            .from('blacklist_v1')
            .select('*')
            .order('created_at', { ascending: false });
            
        if (freshList) setBlacklist(freshList);
        
        showToast(`Processed ${items.length} rules.`);
        return true;
      }
    } catch (error) {
      console.error(error);
      showToast('Failed to bulk add. Some items might be duplicates.', 'error');
      return false;
    }
  };

  const removeFromBlacklist = async (id) => {
    if (!supabaseClientRef.current) return;
    const { error } = await supabaseClientRef.current
      .from('blacklist_v1')
      .delete()
      .eq('id', id);
    if (!error) {
      setBlacklist(prev => prev.filter(b => b.id !== id));
      showToast('Removed from blacklist');
    }
  };

  // Check if an organization is blacklisted
  const isBlacklisted = (orgName) => {
    if (!orgName) return false;
    const lowerOrg = orgName.toLowerCase();
    
    // Check for "Unknown" or generic terms
    if (lowerOrg === 'unknown' || lowerOrg === '' || lowerOrg.includes('unknown')) return true;

    // Check against database blacklist
    return blacklist.some(item => lowerOrg.includes(item.pattern.toLowerCase()));
  };

  return (
    <VisitorContext.Provider value={{ 
      visitors, 
      projects, 
      blacklist, 
      isLoadingDb, 
      isConnected, 
      tablesMissing,
      credentials,
      user, 
      login, 
      logout,
      toast, 
      setToast, 
      connectManual,
      addProject,
      removeProject,
      addToBlacklist,
      addBulkBlacklist,
      removeFromBlacklist,
      isBlacklisted 
    }}>
      {children}
    </VisitorContext.Provider>
  );
};